import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  useTechRadar,
  useSuppliers,
  useAllTeams,
  useTechRadarTeamScope,
} from '../../../graphql/queries';
import { EmptyState } from '../../../components/shared';
import {
  TechRadarChart,
  RING_ORDER,
  RING_COLORS,
  sectorColor,
} from '../../../components/technology/TechRadarChart';
import { TechnologyLifecycleStage, type TechRadarBlip } from '../../../graphql/types';
import '@tylertech/forge/banner';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/card';
import '@tylertech/forge/select';
import './index.css';

export const Route = createFileRoute('/technologies/radar/')({
  component: TechRadarPage,
});

export { TechRadarPage };

const ForgeBanner = 'forge-banner' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;
const ForgeCard = 'forge-card' as React.ElementType;
const ForgeSelect = 'forge-select' as React.ElementType;
const ForgeOption = 'forge-option' as React.ElementType;

/** `/services` search for a team subtree, optionally narrowed to one technology (issue #2097). */
function servicesSearch(
  teamId: string,
  technologyId = ''
): {
  page: number;
  pageSize: number;
  search: string;
  lifecycleStatus: '';
  tier: '';
  tagId: string;
  teamId: string;
  technologyId: string;
  includeSubTeams: true;
} {
  return {
    page: 1,
    pageSize: 25,
    search: '',
    lifecycleStatus: '',
    tier: '',
    tagId: '',
    teamId,
    technologyId,
    includeSubTeams: true,
  };
}

const plural = (n: number, singular: string): string =>
  `${n} ${n === 1 ? singular : `${singular}s`}`;

interface RadarFilters {
  search: string;
  supplierId: string;
  ring: TechnologyLifecycleStage | '';
  /** Team whose subtree (team + sub-teams) of services' technologies is highlighted, or ''. */
  teamId: string;
  /** Active sector label, or '' for all sectors. */
  sector: string;
}

const EMPTY_FILTERS: RadarFilters = {
  search: '',
  supplierId: '',
  ring: '',
  teamId: '',
  sector: '',
};

function TechRadarPage(): JSX.Element {
  const { data, loading, error } = useTechRadar();
  const { data: suppliersData } = useSuppliers();
  const { data: teamsData } = useAllTeams();

  const [filters, setFilters] = useState<RadarFilters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<TechRadarBlip | null>(null);

  // Team scope (ADR-1272, issue #2097): a dedicated query, so blips always come from the single
  // unfiltered `techRadar` query and sectors/buckets stay stable while a team is selected.
  const {
    data: scopeData,
    error: scopeError,
    loading: scopeLoading,
  } = useTechRadarTeamScope(filters.teamId, !filters.teamId);
  // A failed scope query degrades to "no team highlight" (shown inline below the header) rather
  // than replacing the radar, so the user keeps the chart and can change or clear the team.
  const teamScope =
    filters.teamId && !scopeLoading && !scopeError ? scopeData?.techRadarTeamScope : undefined;
  const teamUsage = useMemo(
    () => new Map((teamScope?.usage ?? []).map((u) => [u.technologyId, u.serviceCount])),
    [teamScope]
  );
  const teams = useMemo(
    () =>
      (teamsData?.teams?.edges ?? [])
        .map(({ node }) => node)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [teamsData]
  );

  const sectors = useMemo(() => data?.techRadar?.sectors ?? [], [data]);
  const blips = useMemo(() => data?.techRadar?.blips ?? [], [data]);

  // Active alphabetical bucket (a sector label), or '' for the top-level family-bucket view.
  const activeBucket = filters.sector;

  // Total number of product families across all sectors (tab badges count families, not products).
  const totalFamilies = useMemo(
    () => sectors.reduce((sum, s) => sum + s.families.length, 0),
    [sectors]
  );

  // When a bucket tab is active, the radar repaints: its sectors become the individual product
  // families inside that bucket, and only that bucket's technologies are shown.
  const drillSectors = useMemo(() => {
    if (!activeBucket) return sectors;
    const bucket = sectors.find((s) => s.label === activeBucket);
    return (bucket?.families ?? []).map((family) => ({ label: family, families: [family] }));
  }, [sectors, activeBucket]);

  const visibleBlips = useMemo(
    () => (activeBucket ? blips.filter((b) => b.sector === activeBucket) : blips),
    [blips, activeBucket]
  );

  // In the drilled-in view, place blips by their actual family wedge; otherwise by bucket.
  const sectorOf = useMemo(
    () => (activeBucket ? (b: TechRadarBlip) => b.family : (b: TechRadarBlip) => b.sector),
    [activeBucket]
  );

  // Build the supplier-name lookup so the search can match by name client-side.
  const supplierNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of suppliersData?.suppliers ?? []) {
      map.set(s.entityId, s.name);
    }
    return map;
  }, [suppliersData]);

  // Highlight filters (search/supplier/ring) dim non-matching blips within the current view.
  const highlightActive = Boolean(
    filters.search || filters.supplierId || filters.ring || teamScope
  );

  // Does a blip satisfy the active search/supplier/ring filters? (Ignores the sector tab.)
  const matchesHighlight = useCallback(
    (blip: TechRadarBlip): boolean => {
      if (filters.ring && blip.ring !== filters.ring) return false;
      if (teamScope && !teamUsage.has(blip.id)) return false;
      if (filters.supplierId) {
        const selectedName = supplierNameById.get(filters.supplierId);
        if (!selectedName || blip.supplier !== selectedName) return false;
      }
      const search = filters.search.trim().toLowerCase();
      if (search) {
        const haystack = [blip.name, blip.supplier ?? '', blip.description ?? '']
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    },
    [filters.ring, filters.supplierId, filters.search, supplierNameById, teamScope, teamUsage]
  );

  const matchedIds = useMemo(() => {
    const matched = new Set<string>();
    for (const blip of visibleBlips) {
      if (matchesHighlight(blip)) matched.add(blip.id);
    }
    return matched;
  }, [visibleBlips, matchesHighlight]);

  // How many technologies in each bucket (across all blips) satisfy the active filters.
  const bucketMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const blip of blips) {
      if (matchesHighlight(blip)) counts.set(blip.sector, (counts.get(blip.sector) ?? 0) + 1);
    }
    return counts;
  }, [blips, matchesHighlight]);

  const matchedTotal = useMemo(
    () => Array.from(bucketMatchCounts.values()).reduce((sum, n) => sum + n, 0),
    [bucketMatchCounts]
  );

  // Technology totals per bucket and per family, for the "matched of total" badges.
  const bucketTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const blip of blips) totals.set(blip.sector, (totals.get(blip.sector) ?? 0) + 1);
    return totals;
  }, [blips]);

  const familyCounts = useMemo(() => {
    const counts = new Map<string, { matched: number; total: number }>();
    for (const blip of visibleBlips) {
      const entry = counts.get(blip.family) ?? { matched: 0, total: 0 };
      entry.total += 1;
      if (matchesHighlight(blip)) entry.matched += 1;
      counts.set(blip.family, entry);
    }
    return counts;
  }, [visibleBlips, matchesHighlight]);

  const handleFilterChange = (patch: Partial<RadarFilters>): void => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const handleClear = (): void => setFilters(EMPTY_FILTERS);

  // When filtering, buckets with no matching technologies are disabled; if the current tab has no
  // matches (or All is selected), auto-select the first bucket that does.
  useEffect(() => {
    if (!highlightActive) return;
    const matchingBuckets = sectors
      .map((s) => s.label)
      .filter((label) => (bucketMatchCounts.get(label) ?? 0) > 0);
    if (matchingBuckets.length === 0) return;
    if (!activeBucket || !matchingBuckets.includes(activeBucket)) {
      setFilters((prev) => ({ ...prev, sector: matchingBuckets[0] }));
    }
  }, [highlightActive, bucketMatchCounts, sectors, activeBucket]);

  // A selected team counts even before (or without) its scope: Clear Filters must stay
  // reachable while the scope query is loading or has failed.
  const anyFilterActive = highlightActive || Boolean(activeBucket) || Boolean(filters.teamId);

  if (error) {
    return (
      <div className="tech-radar-page">
        <h1 className="forge-typography--headline4">Tech Radar</h1>
        <div className="tech-radar-page__error">
          <EmptyState
            icon="error"
            title="Failed to load the Tech Radar"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="tech-radar-page">
      <div className="page-header page-header--toolbar page-header--spaced">
        <div className="page-header-title">
          <h1 className="forge-typography--headline4">Tech Radar</h1>
          <span className="forge-typography--body2 tech-radar-page__count">
            {loading
              ? 'Loading…'
              : highlightActive
                ? `${matchedTotal} of ${blips.length} technologies match${
                    activeBucket ? ` · ${matchedIds.size} in ${activeBucket}` : ''
                  }`
                : activeBucket
                  ? `${visibleBlips.length} ${
                      visibleBlips.length === 1 ? 'technology' : 'technologies'
                    } in ${activeBucket}`
                  : `${blips.length} technologies`}
          </span>
          {teamScope && (
            <span className="forge-typography--body2 tech-radar-page__services">
              {' · '}
              <Link
                to="/services"
                search={servicesSearch(filters.teamId)}
                aria-label={`View ${plural(teamScope.serviceCount, 'service')} across ${plural(
                  teamScope.teamCount,
                  'team'
                )}`}
              >
                {plural(teamScope.serviceCount, 'service')} across{' '}
                {plural(teamScope.teamCount, 'team')}
              </Link>
            </span>
          )}
        </div>
      </div>

      {filters.teamId && scopeError && (
        <ForgeBanner
          theme="error"
          dismissed={false}
          role="alert"
          className="tech-radar-page__scope-error"
        >
          Couldn&apos;t load usage for the selected team, so it isn&apos;t highlighted:{' '}
          {scopeError.message}
        </ForgeBanner>
      )}

      {sectors.length > 0 && (
        <div className="tech-radar-tabs" role="tablist" aria-label="Filter by sector">
          <button
            type="button"
            role="tab"
            aria-selected={filters.sector === ''}
            className={`tech-radar-tab${filters.sector === '' ? ' tech-radar-tab--active' : ''}`}
            onClick={() => handleFilterChange({ sector: '' })}
          >
            All families (
            {highlightActive ? `${matchedTotal} of ${blips.length} technologies` : totalFamilies})
          </button>
          {sectors.map((sector) => {
            const noMatches = highlightActive && (bucketMatchCounts.get(sector.label) ?? 0) === 0;
            return (
              <button
                key={sector.label}
                type="button"
                role="tab"
                aria-selected={filters.sector === sector.label}
                disabled={noMatches}
                className={`tech-radar-tab${
                  filters.sector === sector.label ? ' tech-radar-tab--active' : ''
                }`}
                onClick={() => handleFilterChange({ sector: sector.label })}
              >
                {sector.label} (
                {highlightActive
                  ? `${bucketMatchCounts.get(sector.label) ?? 0} of ${
                      bucketTotals.get(sector.label) ?? 0
                    } technologies`
                  : sector.families.length}
                )
              </button>
            );
          })}
        </div>
      )}

      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, supplier, or description…"
            aria-label="Search the radar"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>
        <div className="filter-selects">
          <ForgeSelect
            name="supplierId"
            label="Filter by supplier"
            value={filters.supplierId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange({ supplierId: e.target.value })
            }
          >
            <ForgeOption value="">All Suppliers</ForgeOption>
            {[...(suppliersData?.suppliers ?? [])]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((s) => (
                <ForgeOption key={s.entityId} value={s.entityId}>
                  {s.name}
                </ForgeOption>
              ))}
          </ForgeSelect>

          <ForgeSelect
            name="ring"
            label="Show ring"
            value={filters.ring}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange({ ring: e.target.value as TechnologyLifecycleStage | '' })
            }
          >
            <ForgeOption value="">Show: All Rings</ForgeOption>
            {RING_ORDER.map((ring) => (
              <ForgeOption key={ring} value={ring}>
                Show: {ring}
              </ForgeOption>
            ))}
          </ForgeSelect>

          <div className="tech-radar-team-filter">
            <ForgeSelect
              name="teamId"
              label="Filter by team"
              value={filters.teamId}
              aria-describedby="tech-radar-team-hint"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleFilterChange({ teamId: e.target.value })
              }
            >
              <ForgeOption value="">All Teams</ForgeOption>
              {teams.map((team) => (
                <ForgeOption key={team.entityId} value={team.entityId}>
                  {team.name}
                </ForgeOption>
              ))}
            </ForgeSelect>
            <span id="tech-radar-team-hint" className="forge-typography--caption">
              Includes sub-teams
            </span>
          </div>

          {anyFilterActive && (
            <ForgeButton type="button" variant="outlined" onClick={handleClear}>
              Clear Filters
            </ForgeButton>
          )}
        </div>
      </div>

      {!loading && blips.length === 0 ? (
        <EmptyState
          icon="chart_arc"
          title="No technologies to plot"
          description="Add technologies with a strategic lifecycle stage to populate the radar."
        />
      ) : (
        <div className="tech-radar-layout">
          <div className="tech-radar-layout__chart">
            <TechRadarChart
              sectors={drillSectors}
              blips={visibleBlips}
              matchedIds={matchedIds}
              filtersActive={highlightActive}
              sectorOf={sectorOf}
              selectedId={selected?.id ?? null}
              onSelect={setSelected}
            />
          </div>

          <aside className="tech-radar-layout__side">
            {selected ? (
              <ForgeCard className="tech-radar-detail">
                <div className="tech-radar-detail__header">
                  <h2 className="forge-typography--heading6">{selected.name}</h2>
                  <ForgeButton
                    type="button"
                    variant="text"
                    aria-label="Close detail panel"
                    onClick={() => setSelected(null)}
                  >
                    <forge-icon name="close" aria-hidden="true"></forge-icon>
                  </ForgeButton>
                </div>
                <dl className="tech-radar-detail__list">
                  <dt>Ring</dt>
                  <dd>
                    <span
                      className="tech-radar-detail__ring-dot"
                      style={{ backgroundColor: RING_COLORS[selected.ring] }}
                      aria-hidden="true"
                    />
                    {selected.ring}
                    {selected.isNew && <span className="tech-radar-detail__new"> · New</span>}
                  </dd>
                  <dt>Sector</dt>
                  <dd>{selected.sector}</dd>
                  <dt>Product Family</dt>
                  <dd>{selected.family}</dd>
                  <dt>Supplier</dt>
                  <dd>{selected.supplier ?? '—'}</dd>
                  {selected.description && (
                    <>
                      <dt>Description</dt>
                      <dd>{selected.description}</dd>
                    </>
                  )}
                </dl>
                {teamScope &&
                  (teamUsage.get(selected.id) ? (
                    <p className="tech-radar-detail__usage">
                      <Link to="/services" search={servicesSearch(filters.teamId, selected.id)}>
                        Used by {plural(teamUsage.get(selected.id) ?? 0, 'service')} in this team
                      </Link>
                    </p>
                  ) : (
                    <p className="tech-radar-detail__usage">
                      Not used by this team&apos;s services
                    </p>
                  ))}
                <Link to="/technologies/$technologyId" params={{ technologyId: selected.id }}>
                  View technology details
                </Link>
              </ForgeCard>
            ) : (
              <ForgeCard className="tech-radar-legend">
                <h2 className="forge-typography--heading6">Legend</h2>
                <h3 className="forge-typography--subtitle2">Rings (lifecycle)</h3>
                <ul className="tech-radar-legend__rings">
                  {RING_ORDER.map((ring) => (
                    <li key={ring}>
                      <span
                        className="tech-radar-detail__ring-dot"
                        // ring is from RING_ORDER (TechnologyLifecycleStage enum) — not user-controlled
                        // eslint-disable-next-line security/detect-object-injection
                        style={{ backgroundColor: RING_COLORS[ring] }}
                        aria-hidden="true"
                      />
                      {ring}
                    </li>
                  ))}
                </ul>
                <h3 className="forge-typography--subtitle2">
                  {activeBucket ? `Sectors in ${activeBucket}` : 'Sectors (product families)'}
                </h3>
                <ul className="tech-radar-legend__sectors">
                  {drillSectors.map((sector, i) => (
                    <li key={sector.label}>
                      <span
                        className="tech-radar-legend__sector-swatch"
                        style={{ backgroundColor: sectorColor(i) }}
                        aria-hidden="true"
                      />
                      <strong>{sector.label}</strong>
                      {highlightActive && activeBucket && (
                        <span className="tech-radar-legend__families">
                          {`${familyCounts.get(sector.label)?.matched ?? 0} of ${
                            familyCounts.get(sector.label)?.total ?? 0
                          } technologies`}
                        </span>
                      )}
                      {sector.families.length > 1 && (
                        <span className="tech-radar-legend__families">
                          {sector.families.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="forge-typography--caption tech-radar-legend__hint">
                  Click a blip to see its details. Dashed outline = added in the last 90 days.
                </p>
              </ForgeCard>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
